/**
 * LOS LÍMITES DEL CONOCIMIENTO, TAL COMO LOS APLICA EL BOT (15-oct-2026) — lo puro.
 *
 * EL CASO. Bigwaves pegó sus políticas (7423 caracteres) en el formulario de conocimiento. El
 * `<textarea maxLength={5000}>` las cortó en silencio al pegar: el contador decía «5000 / 5000» y
 * nada avisaba que la garantía legal y el contacto se habían perdido. El bot nunca los vio.
 *
 * LO QUE HACE. Mide cada sección y el total CON LAS REGLAS DEL BOT
 * (`functions/bot/utils/knowledgeNormalizer.js`), no con el largo del textarea:
 *   · el bot junta espacios y saltos de línea (`\s+` → un espacio) antes de medir;
 *   · corta cada sección en 5000 (`MAX_CHARS_PER_SECTION`);
 *   · si el total pasa de 15000 (`MAX_TOTAL_CHARS`) recorta TODAS las secciones en proporción, y
 *     el total cuenta también las restricciones (200 c/u, hasta 20) y los ejemplos (500 por lado,
 *     hasta 6).
 * La paridad con el normalizador real la fija `functions/__tests__/limitesDelConocimiento.parity.jest.test.js`
 * (registrada en `check-shared-module-parity.js`): dice «excede» exactamente cuando el bot corta.
 *
 * CommonJS a propósito (como `botonesDeMensaje.js`): CRA interopera `module.exports`, y así el
 * test vive en `functions/__tests__/`, que es la puerta que corre en el pre-commit.
 */

const MAX_POR_SECCION = 5000;
const MAX_TOTAL = 15000;
const MAX_RESTRICCION = 200;
const MAX_RESTRICCIONES = 20;
const MAX_EJEMPLO = 500;
const MAX_EJEMPLOS = 6;
/** Desde aquí el contador avisa que queda poco (90 %). */
const UMBRAL_CERCA = 0.9;
const SECCIONES = ['company_info', 'products_services', 'faqs', 'policies'];

/** El texto como lo deja el bot antes de medirlo: espacios y saltos juntados. */
const comoLoVeElBot = (t) => String(t || '').replace(/\s+/g, ' ').trim();

/** El largo que queda tras el corte del bot (mismo algoritmo: corta en el último espacio y suma «...»). */
function largoTrasElCorte(texto, max) {
  const t = comoLoVeElBot(texto);
  if (t.length <= max) return t.length;
  const cortado = t.substring(0, max);
  const ultimoEspacio = cortado.lastIndexOf(' ');
  return (ultimoEspacio > max * 0.8 ? ultimoEspacio : max) + 3;
}

function estadoDe(largo, max) {
  if (largo > max) return 'excede';
  if (largo >= max * UMBRAL_CERCA) return 'cerca';
  return 'ok';
}

/**
 * PURA.
 * @param {object} conocimiento  el `knowledge` del bot, tal como lo edita el formulario.
 * @returns {{
 *   secciones: Object<string, {largo:number, max:number, sobra:number, estado:'ok'|'cerca'|'excede'}>,
 *   total: {largo:number, max:number, sobra:number, estado:'ok'|'cerca'|'excede'},
 *   hayExceso: boolean,
 * }}
 *   `largo` es lo que mide el bot; `sobra` es cuánto queda fuera (0 si nada).
 */
function limitesDelConocimiento(conocimiento) {
  const k = conocimiento || {};
  const secciones = {};
  for (const s of SECCIONES) {
    const largo = comoLoVeElBot(k[s]).length;
    secciones[s] = { largo, max: MAX_POR_SECCION, sobra: Math.max(0, largo - MAX_POR_SECCION), estado: estadoDe(largo, MAX_POR_SECCION) };
  }
  // El total del bot se mide con cada sección ya cortada en 5000 (más «...»), más restricciones y ejemplos.
  const restricciones = (Array.isArray(k.restricted_info) ? k.restricted_info : [])
    .filter((x) => typeof x === 'string' && x.trim().length > 0)
    .slice(0, MAX_RESTRICCIONES)
    .reduce((a, x) => a + largoTrasElCorte(x, MAX_RESTRICCION), 0);
  const ejemplos = (Array.isArray(k.conversation_examples) ? k.conversation_examples : [])
    .filter((e) => e && typeof e === 'object' && e.user && e.bot)
    .slice(0, MAX_EJEMPLOS)
    .reduce((a, e) => a + largoTrasElCorte(e.user, MAX_EJEMPLO) + largoTrasElCorte(e.bot, MAX_EJEMPLO), 0);
  const largoTotal = SECCIONES.reduce((a, s) => a + largoTrasElCorte(k[s], MAX_POR_SECCION), 0) + restricciones + ejemplos;
  const total = { largo: largoTotal, max: MAX_TOTAL, sobra: Math.max(0, largoTotal - MAX_TOTAL), estado: estadoDe(largoTotal, MAX_TOTAL) };
  const hayExceso = total.estado === 'excede' || SECCIONES.some((s) => secciones[s].estado === 'excede');
  return { secciones, total, hayExceso };
}

module.exports = { limitesDelConocimiento, comoLoVeElBot, SECCIONES, MAX_POR_SECCION, MAX_TOTAL, UMBRAL_CERCA };
